'use client';

import type { MediaDto } from '@/lib/sanity/dto';
import { IconCaretLeft } from '@/components/icons/IconCaretLeft';
import { IconCaretRight } from '@/components/icons/IconCaretRight';
import { UiImage } from '@/components/ui/UiImage';
import { VEHICLE_IMAGE_SIZES } from '@/features/vehicle';
import { cn } from '@/lib/utils/tw-merge';
import { useThumbStrip } from './useThumbStrip';

type VehicleThumbStripProps = {
    images: MediaDto[];
    activeIndex: number;
    onSelect: (index: number) => void;
    /** The photo's alt text: the button is named by its own label, the image still describes the car. */
    altFor: (index: number) => string;
    /** Inside the full-screen viewer. */
    onDark?: boolean;
};

/** Thumbnails under the gallery and in the viewer; paging buttons appear once it overflows. */
export function VehicleThumbStrip({ images, activeIndex, onSelect, altFor, onDark = false }: VehicleThumbStripProps) {
    const { stripRef, canScrollPrev, canScrollNext, scrollBy } = useThumbStrip(activeIndex);

    return (
        <div className={cn('vehicle-thumbs', onDark && 'vehicle-thumbs--on-dark')}>
            {canScrollPrev ? (
                <>
                    <div className="vehicle-thumbs__fade vehicle-thumbs__fade--prev" aria-hidden="true" />
                    <button
                        type="button"
                        className="vehicle-thumbs__scroll vehicle-thumbs__scroll--prev"
                        aria-label="Weitere Vorschaubilder links"
                        onClick={() => scrollBy(-1)}
                    >
                        <IconCaretLeft width={14} height={14} aria-hidden="true" />
                    </button>
                </>
            ) : null}

            <div ref={stripRef} className="vehicle-thumbs__strip">
                {images.map((image, index) => (
                    <button
                        key={index}
                        type="button"
                        className="vehicle-thumbs__thumb vehicle-media"
                        aria-label={`Bild ${index + 1} von ${images.length} anzeigen`}
                        aria-current={index === activeIndex ? 'true' : undefined}
                        onClick={() => onSelect(index)}
                    >
                        <UiImage image={image} sizes={VEHICLE_IMAGE_SIZES.thumb} alt={altFor(index)} />
                    </button>
                ))}
            </div>

            {canScrollNext ? (
                <>
                    <div className="vehicle-thumbs__fade vehicle-thumbs__fade--next" aria-hidden="true" />
                    <button
                        type="button"
                        className="vehicle-thumbs__scroll vehicle-thumbs__scroll--next"
                        aria-label="Weitere Vorschaubilder rechts"
                        onClick={() => scrollBy(1)}
                    >
                        <IconCaretRight width={14} height={14} aria-hidden="true" />
                    </button>
                </>
            ) : null}
        </div>
    );
}
